import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const FONT_STYLES = [
  { value: 'professional', label: 'Professional', preview: 'font-sans' },
  { value: 'modern', label: 'Modern', preview: 'font-sans tracking-tight' },
  { value: 'classic', label: 'Classic', preview: 'font-serif' },
  { value: 'minimal', label: 'Minimal', preview: 'font-mono' },
];

const BrandingPanel = ({ template, onChange, orgId }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!validTypes?.includes(file?.type)) {
      toast?.error('Please upload a JPG, PNG, WebP, or SVG image');
      return;
    }
    if (file?.size > 2 * 1024 * 1024) {
      toast?.error('Logo must be under 2MB');
      return;
    }

    setUploading(true);
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `${orgId}/template-logos/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase?.storage?.from('contact-attachments')?.upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: signedData } = await supabase?.storage?.from('contact-attachments')?.createSignedUrl(path, 60 * 60 * 24 * 365);
      const { data: urlData } = supabase?.storage?.from('contact-attachments')?.getPublicUrl(path);

      const logoUrl = signedData?.signedUrl || urlData?.publicUrl;
      onChange({ logo_url: logoUrl });
      toast?.success('Logo uploaded!');
    } catch (err) {
      toast?.error('Upload failed: ' + err?.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo Upload */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-2 uppercase tracking-wider">
          Company Logo
        </label>
        <div className="flex items-start gap-4">
          <div
            className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden shrink-0 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileRef?.current?.click()}
          >
            {template?.logo_url ? (
              <img
                src={template?.logo_url}
                alt="Company logo"
                className="w-full h-full object-contain p-1"
              />
            ) : (
              <div className="text-center">
                <Icon name="Image" size={20} className="text-muted-foreground mx-auto mb-1" />
                <span className="text-xs text-muted-foreground">Logo</span>
              </div>
            )}
          </div>

          <div className="flex-1">
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.svg"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef?.current?.click()}
              disabled={uploading}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Icon name="Upload" size={14} />
                  {template?.logo_url ? 'Change Logo' : 'Upload Logo'}
                </>
              )}
            </button>
            {template?.logo_url && (
              <button
                type="button"
                onClick={() => onChange({ logo_url: null })}
                className="w-full mt-1.5 px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                Remove Logo
              </button>
            )}
            <p className="text-xs text-muted-foreground mt-1.5">PNG, JPG, SVG · Max 2MB</p>
          </div>
        </div>

        {/* Show logo toggle */}
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={() => onChange({ show_logo: !template?.show_logo })}
            className={`relative w-9 h-5 rounded-full transition-colors ${template?.show_logo !== false ? 'bg-primary' : 'bg-muted'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${template?.show_logo !== false ? 'translate-x-4' : ''}`} />
          </button>
          <span className="text-xs text-muted-foreground">Show logo on document</span>
        </div>
      </div>

      {/* Font Style */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-2 uppercase tracking-wider">
          Document Style
        </label>
        <div className="grid grid-cols-2 gap-2">
          {FONT_STYLES?.map(style => (
            <button
              key={style?.value}
              type="button"
              onClick={() => onChange({ font_style: style?.value })}
              className={`px-3 py-2.5 rounded-lg border text-sm transition-all text-left ${
                template?.font_style === style?.value
                  ? 'border-primary bg-primary/5 text-primary font-medium' :'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
              }`}
            >
              <span className={style?.preview}>{style?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Payment Terms */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-2 uppercase tracking-wider">
          Payment Terms
        </label>
        <select
          value={template?.payment_terms || 'Due upon completion'}
          onChange={e => onChange({ payment_terms: e?.target?.value })}
          className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option>Due upon completion</option>
          <option>Net 15</option>
          <option>Net 30</option>
          <option>Net 45</option>
          <option>Net 60</option>
          <option>50% upfront, 50% on completion</option>
          <option>Deposit required</option>
          <option>COD - Cash on Delivery</option>
        </select>
      </div>
    </div>
  );
};

export default BrandingPanel;
